import { buildWabunkaTagViewModel, createDefaultWabunkaTagViewModel, type WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';
import { isThemeTag } from '@/wabunka-src/common/models/themeTag';
import { EXPERIENCE_CARD_THEME_TAG_MAX_DISPLAY } from '@/wabunka-src/features/Experience/constants/themeTagFilter';

import type { WabunkaExperienceTag } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_tag';

/**
 * 体験に紐づくWabunka タグビューモデル
 */
export interface WabunkaExperienceTagViewModel {
  /** 体験マスタID */
  experienceId: string;
  /** タグ */
  tag: WabunkaTagViewModel;
}

/**
 * Wabunka タグビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceTagViewModel} - Wabunka タグビューモデル のデフォルト値
 */
export const createDefaultWabunkaExperienceTagViewModel = (): WabunkaExperienceTagViewModel => ({
  experienceId: '',
  tag: createDefaultWabunkaTagViewModel(),
});

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaTag} res - APIレスポンス
 * @returns {WabunkaTagViewModel} - ビューモデル
 */
export const buildWabunkaExperienceTagViewModel = (res: WabunkaExperienceTag | undefined): WabunkaExperienceTagViewModel => {
  if (!res) return createDefaultWabunkaExperienceTagViewModel();
  return {
    experienceId: res.experienceId,
    tag: buildWabunkaTagViewModel(res.tag),
  };
};

/**
 * リスト表示用のビューモデル
 */
export type WabunkaExperienceTagListViewModel = WabunkaExperienceTagViewModel[];

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceTagListViewModel = (tags: WabunkaExperienceTag[] | undefined): WabunkaExperienceTagListViewModel => {
  if (!tags) {
    return [];
  }

  return tags.map(buildWabunkaExperienceTagViewModel);
};

// （Other places (エリア名)』のnameの場合は、エリア名のみを返す
export const displayDestinationTagName = (tags: WabunkaExperienceTagListViewModel): string => {
  // 行き先を表すタグは運用上１つのみのため、最初の要素を取得している
  const destinationTag = tags.find((tag) => tag.tag.tagGroupType === 'destination');
  // 行き先タグが見つかった場合、その名前を返し、見つからない場合は空文字を返す
  if (!destinationTag) return '';

  // （Other places (エリア名)』のnameの場合は、エリア名のみを返す
  const match = destinationTag.tag.name.match(/^Other places \((.*?)\)$/);
  return match ? match[1] : destinationTag.tag.name;
};

// 並べ替えず API の順（体験タグの sequence_id 昇順）のまま打ち切る
export const extractDisplayThemeTags = (tags: WabunkaExperienceTagListViewModel): WabunkaTagViewModel[] => {
  const result: WabunkaTagViewModel[] = [];
  const seenIds = new Set<string>();
  for (const t of tags) {
    if (seenIds.has(t.tag.id)) continue;
    seenIds.add(t.tag.id);

    if (!isThemeTag(t.tag)) continue;

    result.push(t.tag);
  }

  return result.slice(0, EXPERIENCE_CARD_THEME_TAG_MAX_DISPLAY);
};
